import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Calendar, Users, FileText, Image, Star, ImagePlus, LogOut, MessageSquare, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-soft">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
          <div className="font-heading text-3xl mt-2 text-slate-900">{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
        <div className="h-10 w-10 rounded-xl bg-primary/8 text-primary flex items-center justify-center"><Icon className="w-5 h-5"/></div>
      </div>
    </div>
  );
}

function AppointmentsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/appointments").then(({data})=>setItems(data));
  useEffect(()=>{ load(); },[]);
  const setStatus = async (id, status) => { await api.patch(`/appointments/${id}`, { status }); toast.success("Updated"); load(); };
  const del = async (id) => { await api.delete(`/appointments/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
          <tr><th className="p-3 text-left">Patient</th><th className="p-3">Phone</th><th className="p-3">Date</th><th className="p-3">Problem</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
        </thead>
        <tbody>
          {items.map(a=>(
            <tr key={a.id} className="border-t border-slate-100" data-testid={`appt-row-${a.id}`}>
              <td className="p-3"><div className="font-medium">{a.name}</div><div className="text-xs text-slate-500">{a.age}y · {a.gender}</div></td>
              <td className="p-3">{a.phone}</td>
              <td className="p-3">{a.preferred_date} {a.preferred_time}</td>
              <td className="p-3">{a.problem}</td>
              <td className="p-3"><Badge variant={a.status==="confirmed"?"default":a.status==="rejected"?"destructive":"secondary"}>{a.status}</Badge></td>
              <td className="p-3 space-x-2">
                {a.status!=="confirmed" && <button onClick={()=>setStatus(a.id,"confirmed")} className="text-xs px-2 py-1 rounded bg-teal text-white">Confirm</button>}
                {a.status!=="rejected" && <button onClick={()=>setStatus(a.id,"rejected")} className="text-xs px-2 py-1 rounded bg-amber-500 text-white">Reject</button>}
                <button onClick={()=>del(a.id)} className="text-xs px-2 py-1 rounded bg-red-500 text-white">Delete</button>
              </td>
            </tr>
          ))}
          {!items.length && <tr><td colSpan="6" className="p-8 text-center text-slate-400">No appointments yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function CRUDTab({ resource, fields, defaults }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(defaults);
  const [editId, setEditId] = useState(null);
  const load = () => api.get(`/${resource}`).then(({data})=>setItems(data||[]));
  useEffect(()=>{ load(); },[resource]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/${resource}/${editId}`, form);
      else await api.post(`/${resource}`, form);
      toast.success(editId?"Updated":"Created");
      setForm(defaults); setEditId(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { await api.delete(`/${resource}/${id}`); load(); };
  const edit = (it) => { setEditId(it.id); setForm({...defaults, ...it}); };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
        <div className="font-heading text-lg">{editId?"Edit":"Create"} item</div>
        {fields.map(f=>(
          <div key={f.name}>
            <Label>{f.label}</Label>
            {f.type==="textarea" ? (
              <Textarea rows={4} value={form[f.name]||""} onChange={e=>setForm({...form,[f.name]:e.target.value})} className="mt-2"/>
            ) : (
              <Input type={f.type||"text"} value={form[f.name]||""} onChange={e=>setForm({...form,[f.name]: f.type==="number"?Number(e.target.value):e.target.value})} className="mt-2"/>
            )}
          </div>
        ))}
        <div className="flex gap-2">
          <Button type="submit" className="rounded-full bg-primary hover:bg-primary-dark">{editId?"Update":"Create"}</Button>
          {editId && <Button type="button" variant="outline" onClick={()=>{setEditId(null);setForm(defaults);}} className="rounded-full">Cancel</Button>}
        </div>
      </form>
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="font-heading text-lg mb-3">Items ({items.length})</div>
        <ul className="divide-y divide-slate-100 max-h-[520px] overflow-auto">
          {items.map(it=>(
            <li key={it.id} className="py-3 flex justify-between items-start gap-3">
              <div className="text-sm">
                <div className="font-medium text-slate-900">{it.title || it.question || it.name}</div>
                <div className="text-xs text-slate-500 mt-1 line-clamp-2">{it.excerpt || it.answer || it.story || it.problem}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={()=>edit(it)} className="text-xs px-2 py-1 rounded bg-slate-100">Edit</button>
                <button onClick={()=>del(it.id)} className="text-xs px-2 py-1 rounded bg-red-500 text-white">Delete</button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout, checking } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(()=>{
    if (user && user!==false) api.get("/stats").then(({data})=>setStats(data));
  }, [user]);

  if (checking) return <main className="pt-32 container-x">Loading…</main>;
  if (!user || user===false) return <Navigate to="/admin/login" replace/>;

  return (
    <main className="min-h-screen bg-slate-50 pt-24 pb-12" data-testid="admin-dashboard">
      <div className="container-x">
        <div className="flex justify-between items-center mb-8">
          <div>
            <span className="overline">Admin</span>
            <h1 className="font-heading text-4xl font-medium mt-2">Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <Link to="/" className="text-sm text-slate-600 hover:text-primary py-2 px-4">View Site</Link>
            <Button variant="outline" onClick={logout} className="rounded-full" data-testid="admin-logout"><LogOut className="w-4 h-4 mr-1"/>Logout</Button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Calendar} label="Appointments" value={stats.appointments_total} hint={`${stats.appointments_pending} pending`}/>
            <StatCard icon={Users} label="Testimonials" value={stats.testimonials}/>
            <StatCard icon={FileText} label="Blog Posts" value={stats.blogs}/>
            <StatCard icon={Image} label="Gallery Items" value={stats.gallery}/>
          </div>
        )}

        {stats?.weekly && (
          <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-6 shadow-soft">
            <div className="font-heading text-lg mb-4">Appointments — last 7 days</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.weekly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="day" tick={{fontSize:12}}/>
                  <YAxis allowDecimals={false} tick={{fontSize:12}}/>
                  <Tooltip/>
                  <Bar dataKey="count" fill="#0F4C81" radius={[6,6,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <Tabs defaultValue="appointments" className="mt-8">
          <TabsList className="bg-white border border-slate-100 rounded-full p-1 flex-wrap h-auto">
            <TabsTrigger value="appointments" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"><Calendar className="w-4 h-4 mr-1"/>Appointments</TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"><Star className="w-4 h-4 mr-1"/>Testimonials</TabsTrigger>
            <TabsTrigger value="before-after" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"><ImagePlus className="w-4 h-4 mr-1"/>Before/After</TabsTrigger>
            <TabsTrigger value="blogs" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"><FileText className="w-4 h-4 mr-1"/>Blogs</TabsTrigger>
            <TabsTrigger value="gallery" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"><Image className="w-4 h-4 mr-1"/>Gallery</TabsTrigger>
            <TabsTrigger value="videos" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"><MessageSquare className="w-4 h-4 mr-1"/>Videos</TabsTrigger>
            <TabsTrigger value="faqs" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"><HelpCircle className="w-4 h-4 mr-1"/>FAQs</TabsTrigger>
          </TabsList>

          <TabsContent value="appointments" className="mt-6"><AppointmentsTab/></TabsContent>
          <TabsContent value="testimonials" className="mt-6">
            <CRUDTab resource="testimonials" defaults={{name:"",age:0,rating:5,story:"",surgery:"",visit_date:"",photo_url:"",video_url:""}}
              fields={[
                {name:"name",label:"Patient Name"},{name:"age",label:"Age",type:"number"},{name:"rating",label:"Rating (1-5)",type:"number"},
                {name:"surgery",label:"Surgery"},{name:"visit_date",label:"Visit Date (YYYY-MM-DD)"},
                {name:"story",label:"Story",type:"textarea"},{name:"photo_url",label:"Photo URL"},{name:"video_url",label:"Video URL"}
              ]}/>
          </TabsContent>
          <TabsContent value="before-after" className="mt-6">
            <CRUDTab resource="before-after" defaults={{name:"",age:0,problem:"",treatment:"",recovery_time:"",result:"",before_url:"",after_url:""}}
              fields={[
                {name:"name",label:"Patient Name (optional)"},{name:"age",label:"Age",type:"number"},
                {name:"problem",label:"Problem",type:"textarea"},{name:"treatment",label:"Treatment"},
                {name:"recovery_time",label:"Recovery Time"},{name:"result",label:"Result"},
                {name:"before_url",label:"Before Image URL"},{name:"after_url",label:"After Image URL"}
              ]}/>
          </TabsContent>
          <TabsContent value="blogs" className="mt-6">
            <CRUDTab resource="blogs" defaults={{title:"",slug:"",category:"",excerpt:"",content:"",cover_url:"",published:true}}
              fields={[
                {name:"title",label:"Title"},{name:"slug",label:"Slug (url-friendly)"},{name:"category",label:"Category"},
                {name:"excerpt",label:"Excerpt",type:"textarea"},{name:"content",label:"Content",type:"textarea"},{name:"cover_url",label:"Cover Image URL"}
              ]}/>
          </TabsContent>
          <TabsContent value="gallery" className="mt-6">
            <CRUDTab resource="gallery" defaults={{title:"",category:"",image_url:""}}
              fields={[{name:"title",label:"Title"},{name:"category",label:"Category"},{name:"image_url",label:"Image URL"}]}/>
          </TabsContent>
          <TabsContent value="videos" className="mt-6">
            <CRUDTab resource="videos" defaults={{title:"",youtube_id:"",description:""}}
              fields={[{name:"title",label:"Title"},{name:"youtube_id",label:"YouTube Video ID"},{name:"description",label:"Description",type:"textarea"}]}/>
          </TabsContent>
          <TabsContent value="faqs" className="mt-6">
            <CRUDTab resource="faqs" defaults={{question:"",answer:"",order:0}}
              fields={[{name:"question",label:"Question"},{name:"answer",label:"Answer",type:"textarea"},{name:"order",label:"Order",type:"number"}]}/>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
